/**
 * Build the crawler-safe share URL for a listing.
 *
 * Returns the SEO-friendly directory-format prerender URL:
 *   https://localbest.ai/api/og/directory/{category}/{city-state}/{slug}
 *
 * That backend route returns the correct per-listing OG/Twitter meta tags
 * (so iMessage, Facebook, LinkedIn, Slack, SMS, etc. show the right preview),
 * and redirects real users to the public listing page.
 *
 * Falls back to /api/og/business/{slug} if the listing has no category/location.
 */
export function buildListingShareUrl(business, { origin = "https://localbest.ai" } = {}) {
  if (!business || !business.slug) return origin;

  const slug = business.slug;
  const categoryId = (business.category_id || "").toLowerCase().trim();
  const citySlug = (business.city || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const stateSlug = (business.state || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  if (categoryId && citySlug) {
    const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
    return `${origin}/api/og/directory/${categoryId}/${cityState}/${slug}`;
  }
  // Graceful fallback — still hits a working OG endpoint.
  return `${origin}/api/og/business/${slug}`;
}

/**
 * Build the human-friendly canonical listing URL.
 * Used for in-page Copy Link buttons and QR codes (where you'd rather
 * show a readable URL than the /api/og/... share URL).
 */
export function buildListingCanonicalUrl(business, { origin = "https://localbest.ai" } = {}) {
  if (!business || !business.slug) return origin;
  const slug = business.slug;
  const categoryId = (business.category_id || "business").toLowerCase().trim();
  const citySlug = (business.city || "local").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const stateSlug = (business.state || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
  return `${origin}/directory/${categoryId}/${cityState}/${slug}`;
}
